import React, { useState } from 'react';
import {
  Box, Card, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Button, Typography, Chip, IconButton, Tooltip, Paper, Avatar
} from '@mui/material';
import {
  Sync, PersonAdd, CheckCircle, ErrorOutline, 
  MailOutline, Badge, MoreVert, LinkOff
} from '@mui/icons-material';

// --- MOCK DATA FROM CUSTOMER SERVER ---
const INITIAL_USERS = [
  { username: 'm_saheem', email: 'saheem@nrgfuel.ie', role: 'Administrator', inConsoleDb: true },
  { username: 'j_driver_01', email: 'john.d@nrgfuel.ie', role: 'Driver', inConsoleDb: true },
  { username: 'b_logistics', email: 'barry@nrgfuel.ie', role: 'Dispatcher', inConsoleDb: false },
  { username: 'temp_driver', email: 'temp@nrgfuel.ie', role: 'Driver', inConsoleDb: false },
];

export const PersonnelSync = ({ organization }) => {
  const [users, setUsers] = useState(INITIAL_USERS);

  const handleSyncUser = (username) => {
    // Logic: Map this user to the Central Console DB
    console.log(`Mapping ${username} to Central Console...`);
    setUsers(prev => prev.map(u => u.username === username ? { ...u, inConsoleDb: true } : u));
  };

  return (
    <Box>
      <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center' ,mb: 2, mt: 1 }}>
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 800, letterSpacing: -0.5 }}>
            Identity Mapping
          </Typography>
          {/* <Typography variant="body2" color="text.secondary">
            Synchronizing <strong>{organization.name}</strong> personnel with Central Console credentials.
          </Typography> */}
        </Box>
        <Button 
          variant="contained" 
          startIcon={<PersonAdd />} 
          sx={{ borderRadius: 1, px: 3, bgcolor: 'primary.main' }}
        >
          Register Personnel
        </Button>
      </Box>

      <Card variant="outlined" sx={{ borderRadius: 1, border: '1px solid rgba(47, 114, 124, 0.2)' }}>
        <TableContainer>
          <Table size="small">
            <TableHead sx={{ bgcolor: '#f8fafc' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 800, py: 2 }}>USER IDENTIFIER</TableCell>
                <TableCell sx={{ fontWeight: 800 }}>SYSTEM ROLE</TableCell>
                <TableCell align="center" sx={{ fontWeight: 800 }}>CONSOLE STATUS</TableCell>
                <TableCell align="right" sx={{ fontWeight: 800, pr: 4 }}>OPERATIONS</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((user) => (
                <TableRow key={user.username} hover sx={{ borderBottom: '1px solid #f1f5f9' }}>
                  {/* USERNAME & EMAIL */}
                  <TableCell sx={{ py: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Avatar 
                        variant="square" 
                        sx={{ 
                          width: 36, height: 36, 
                          bgcolor: user.inConsoleDb ? 'primary.main' : 'text.disabled',
                          borderRadius: 0.5,
                          fontSize: '0.9rem',
                          fontWeight: 800
                        }}
                      >
                        {user.username.charAt(0).toUpperCase()}
                      </Avatar>
                      <Box>
                        <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color: 'text.primary' }}>
                          {user.username}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <MailOutline sx={{ fontSize: 12, color: 'text.disabled' }} />
                          <Typography variant="caption" color="text.secondary">
                            {user.email}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </TableCell>

                  {/* ROLE */}
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Badge sx={{ fontSize: 16, color: 'secondary.main' }} />
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{user.role}</Typography>
                    </Box>
                  </TableCell>

                  {/* CONSOLE STATUS */}
                  <TableCell align="center">
                    {user.inConsoleDb ? (
                      <Chip 
                        icon={<CheckCircle style={{ color: '#16a34a', fontSize: 14 }} />}
                        label="Linked" 
                        size="small" 
                        variant="outlined"
                        sx={{ 
                          borderRadius: 1, 
                          color: '#16a34a', 
                          borderColor: '#bbf7d0', 
                          bgcolor: '#f0fdf4',
                          fontWeight: 700,
                          fontSize: '0.65rem'
                        }} 
                      />
                    ) : (
                      <Chip 
                        icon={<LinkOff style={{ color: '#dc2626', fontSize: 14 }} />}
                        label="Unmapped" 
                        size="small" 
                        sx={{ 
                          borderRadius: 1, 
                          bgcolor: '#fee2e2', 
                          color: '#dc2626',
                          fontWeight: 700,
                          fontSize: '0.65rem'
                        }} 
                      />
                    )}
                  </TableCell>

                  {/* ACTIONS */}
                  <TableCell align="right" sx={{ pr: 2 }}>
                    {user.inConsoleDb ? (
                      <Tooltip title="View Mapping Details">
                        <IconButton size="small"><MoreVert fontSize="small" /></IconButton>
                      </Tooltip>
                    ) : (
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={<Sync />}
                        onClick={() => handleSyncUser(user.username)}
                        sx={{ 
                          borderRadius: 1, 
                          fontSize: '0.7rem', 
                          textTransform: 'uppercase',
                          bgcolor: 'secondary.main',
                          '&:hover': { bgcolor: 'primary.main' }
                        }}
                      >
                        Sync to Console
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      <Box sx={{ mt: 3, p: 2, bgcolor: '#f8fafc', borderLeft: '4px solid', borderLeftColor: 'secondary.main' }}>
        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center', gap: 1 }}>
          <ErrorOutline sx={{ fontSize: 14 }} />
          Personnel marked as <strong>Unmapped</strong> exist in the Customer Database but cannot access the Central Console until synchronized.
        </Typography>
      </Box>
    </Box>
  );
};